import { Link } from '@tanstack/react-router'
import { useState, useRef, useEffect } from 'react'
import type { ReactNode } from 'react'
import { useAuth } from '../auth'
import { useTheme } from '../theme'
import 'remixicon/fonts/remixicon.css'
import styles from './AdminLayout.module.css'

interface AdminLayoutProps {
  children: ReactNode
  title?: string
}

interface NavItem {
  label: string
  path: string
  icon: string  // Remix icon class name
}

const navItems: NavItem[] = [
  { label: 'DASHBOARD', path: '/admin', icon: 'ri-dashboard-line' },
  { label: 'ORDERS', path: '/admin/orders', icon: 'ri-shopping-bag-line' },
  { label: 'DRIVERS', path: '/admin/drivers', icon: 'ri-truck-line' },
  { label: 'USERS', path: '/admin/users', icon: 'ri-user-line' },
  { label: 'DELIVERY ZONES', path: '/admin/delivery-zones', icon: 'ri-map-2-line' },
  { label: 'SETTINGS', path: '/admin/settings', icon: 'ri-settings-3-line' },
]

export function AdminLayout({ children, title = 'ADMIN' }: AdminLayoutProps) {
  const { user, logout } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const sidebarRef = useRef<HTMLElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
        setSidebarOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleLogout = () => {
    logout()
  }

  return (
    <div className={styles.layout}>
      {/* Mobile Overlay */}
      <div
        className={`${styles.mobileOverlay} ${!sidebarOpen ? styles.mobileOverlayHidden : ''}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Sidebar */}
      <nav
        ref={sidebarRef}
        className={`${styles.sidebar} ${sidebarOpen ? styles.sidebarOpen : ''}`}
        aria-label="Admin navigation"
      >
        <div className={styles.brand}>
          <img src="/brand/logomark.png" alt="" className={styles.brandLogo} />
          <div>
            <h2 className={styles.brandName}>TASAMAGO</h2>
            <span className={styles.brandMeta}>OPERATIONS CONSOLE</span>
          </div>
        </div>
        <ul className={styles.navList}>
          {navItems.map((item) => (
            <li key={item.path} className={styles.navItem}>
              <Link
                to={item.path}
                className={styles.navLink}
                onClick={() => setSidebarOpen(false)}
              >
                <i className={`${item.icon} ${styles.navIcon}`} />
                {item.label}
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      {/* Main Content Area */}
      <div className={styles.content}>
        {/* Header */}
        <header className={styles.header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button
              className={styles.hamburger}
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label="Toggle menu"
            >
              <i className="ri-menu-line" />
            </button>
            <h1 className={styles.pageTitle}>{title}</h1>
          </div>
          <div className={styles.userSection}>
            <span className={styles.userName}>{user?.name}</span>
            <button className={styles.themeButton} onClick={toggleTheme} aria-label="Toggle color theme">
              {theme === 'dark' ? 'LIGHT' : 'DARK'}
            </button>
            <button className={styles.logoutButton} onClick={handleLogout}>
              LOGOUT
            </button>
          </div>
        </header>

        {/* Main Content */}
        <main className={styles.main}>
          {children}
        </main>
      </div>
    </div>
  )
}
